import React, { PureComponent } from "react";
import ReactDOM from "react-dom";
import * as Icon from "react-feather";

import NativeCell from "./NativeCell";
import PortalMenu from "./PortalMenu";
import UserAssigner from "../common/user_assigner/UserAssigner";
import SearchableDropdown from "../common/searchable_dropdown/SearchableDropdown";
import DescriptionField from "../workflow/components/description_field/DescriptionField";
import { TASK_STATUSES, USER_STATUSES } from "./subgridColumns";

/**
 * Renders one NATIVE cell for a task/user sub-grid row.
 *
 * Simple types (text/date/checkbox) delegate to NativeCell. Richer types
 * (status/role/client/email/assignees/description) are handled here, with
 * complex editors rendered in a body-portaled popover so the sub-grid's
 * horizontal scroll never clips them.
 *
 * onChange(value) is called for native field edits. For assignees, value is the
 * full assignedUsers array.
 */
class SubGridCell extends PureComponent {
	constructor(props) {
		super(props);
		this.state = { open: false, pos: null, dropOpen: false };
		this._anchorRef = React.createRef();
	}

	_openPopover = () => {
		const el = this._anchorRef.current;
		const rect = el ? el.getBoundingClientRect() : { left: 0, bottom: 0, width: 260 };
		this.setState({ open: true, pos: { left: rect.left, top: rect.bottom + 4, width: Math.max(rect.width, 280) } });
	};

	_closePopover = () => this.setState({ open: false });

	renderPopover(content) {
		if (!this.state.open || !this.state.pos) return null;
		const { left, top, width } = this.state.pos;
		return ReactDOM.createPortal(
			<>
				<div className="et-portal-backdrop" onClick={this._closePopover} />
				<div className="et-portal-pop" style={{ left, top, width }}>
					{content}
				</div>
			</>,
			document.body,
		);
	}

	// ---------- generic pill select (status/role) ----------

	renderSelect(options, selectedKey, onPick, { allowClear } = {}) {
		const selected = options.find(o => String(o.key) === String(selectedKey));
		const { dropOpen } = this.state;
		const pillStyle = selected && selected.color ? { background: selected.color } : undefined;
		return (
			<div className="et-cell__dropdown">
				<button
					ref={this._anchorRef}
					className={"et-pill " + (selected ? "" : "et-pill--empty")}
					style={pillStyle}
					onClick={() => this.setState({ dropOpen: !dropOpen })}
					data-tracker-focus
				>
					<span className="et-pill__label">{selected ? selected.label : "—"}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>
				{dropOpen && (
					<PortalMenu anchorRef={this._anchorRef} onClose={() => this.setState({ dropOpen: false })}>
						{options.length === 0 && <div className="et-cell__menu-empty">No options</div>}
						{options.map(o => (
							<button
								key={o.key}
								className={"et-cell__menu-item " + (String(o.key) === String(selectedKey) ? "et-cell__menu-item--active" : "")}
								onClick={() => {
									this.setState({ dropOpen: false });
									onPick(o.key);
								}}
							>
								<span className="et-cell__menu-swatch" style={{ background: o.color || "#eef0f4" }} />
								<span className="et-cell__menu-label">{o.label}</span>
							</button>
						))}
						{allowClear && selectedKey != null && selectedKey !== "" && (
							<>
								<div className="et-cell__menu-divider" />
								<button
									className="et-cell__menu-item et-cell__menu-item--clear"
									onClick={() => {
										this.setState({ dropOpen: false });
										onPick(null);
									}}
								>
									<Icon.X size={12} className="et-cell__menu-swatch-clear" />
									<span>Clear</span>
								</button>
							</>
						)}
					</PortalMenu>
				)}
			</div>
		);
	}

	roleOptions() {
		return (this.props.roles || []).map(r => ({ key: r.id, label: r.name, color: "#dfe3eb" }));
	}

	// ---------- assignees ----------

	renderAssignees() {
		const list = Array.isArray(this.props.value) ? this.props.value : [];
		const usersForAssigner = list.map(a => ({
			user_id: a.user_id,
			role_id: a.role_id,
			userName: a.name,
			roleName: a.role_name,
			should_notify: !!a.should_notify,
		}));
		return (
			<>
				<button className="et-subcell-chips" ref={this._anchorRef} onClick={this._openPopover} data-tracker-focus>
					{list.length === 0 ? (
						<span className="et-cell__placeholder">— add —</span>
					) : (
						list.slice(0, 3).map((a, i) => (
							<span className="et-chip" key={i} title={a.name}>
								{a.name || "User"}
							</span>
						))
					)}
					{list.length > 3 && <span className="et-chip et-chip--more">+{list.length - 3}</span>}
				</button>
				{this.renderPopover(
					<UserAssigner
						users={usersForAssigner}
						showNotificationToggle={true}
						onChange={({ selectedUsers }) =>
							this.props.onChange(
								(selectedUsers || []).map(u => ({ user_id: u.user_id, role_id: u.role_id, should_notify: !!u.should_notify })),
							)
						}
					/>,
				)}
			</>
		);
	}

	// ---------- description ----------

	renderDescription() {
		const html = this.props.value || "";
		const text = String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
		return (
			<>
				<button className="et-subcell-rich" ref={this._anchorRef} onClick={this._openPopover} data-tracker-focus>
					{text ? <span className="et-subcell-rich__preview">{text}</span> : <span className="et-cell__placeholder">—</span>}
					<Icon.Edit2 size={11} className="et-subcell-rich__icon" />
				</button>
				{this.renderPopover(
					<div className="et-rich-pop">
						<DescriptionField value={html} onChange={val => this._pendingDesc = val} />
						<div className="et-rich-pop__actions">
							<button className="et-btn et-btn--ghost et-btn--sm" onClick={this._closePopover}>
								Cancel
							</button>
							<button
								className="et-btn et-btn--primary et-btn--sm"
								onClick={() => {
									if (this._pendingDesc !== undefined) this.props.onChange(this._pendingDesc);
									this._closePopover();
								}}
							>
								Save
							</button>
						</div>
					</div>,
				)}
			</>
		);
	}

	// ---------- client ----------

	renderClient() {
		const { value, clientLabels } = this.props;
		const label = value && clientLabels ? clientLabels[value] : null;
		return (
			<>
				<button className={"et-pill " + (label ? "" : "et-pill--empty")} ref={this._anchorRef} onClick={this._openPopover} data-tracker-focus>
					<span className="et-pill__label">{label || "—"}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>
				{this.renderPopover(
					<div className="et-client-pop">
						<SearchableDropdown
							type="clients"
							selectedName={label || ""}
							onSelect={c => {
								this.props.onChange(c ? c.id : null);
								this._closePopover();
							}}
						/>
					</div>,
				)}
			</>
		);
	}

	render() {
		const { type, value, onChange, isAdmin, readonly } = this.props;
		if (readonly) {
			return <span className="et-subcell-readonly">{value || "—"}</span>;
		}
		switch (type) {
			case "status":
				return this.renderSelect(TASK_STATUSES, value, v => onChange(v));
			case "user_status":
				return this.renderSelect(USER_STATUSES, value, v => onChange(v));
			case "role":
				return this.renderSelect(this.roleOptions(), value, v => onChange(v), { allowClear: true });
			case "member_role":
				return this.renderSelect(this.roleOptions(), value, v => onChange(v));
			case "assignees":
				return this.renderAssignees();
			case "description":
				return this.renderDescription();
			case "client":
				return this.renderClient();
			case "email":
				// Editable only by admins; otherwise read-only text.
				if (!isAdmin) return <span className="et-subcell-readonly">{value || "—"}</span>;
				return <NativeCell type="text" value={value} onChange={onChange} />;
			case "date":
				return <NativeCell type="date" value={value} onChange={onChange} />;
			case "checkbox":
				return <NativeCell type="checkbox" value={value} onChange={onChange} />;
			case "text":
			default:
				return <NativeCell type="text" value={value} onChange={onChange} />;
		}
	}
}

export default SubGridCell;
